<template>
  <div>
    <Pane />

    <div class="detail-container" v-if="detail.id">
      <!-- 顶部标题栏 -->
      <div class="header-card">
        <div class="project-title">{{ detail.projectName }}</div>
        <div class="header-meta">
          <div class="meta-item">
            <span class="meta-label">业主单位</span>
            <span class="meta-value">{{ detail.customerName || '—' }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">联系人</span>
            <span class="meta-value">{{ detail.contactName || '—' }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">备案状态</span>
            <span class="meta-value">{{ filingStatusText }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">项目属性</span>
            <span class="meta-value">{{ detail.projectProperty }}</span>
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <a-card class="content-card">
        <div class="tab-toolbar" v-if="canEditByAdmin(this.detail.status)">
          <permission-button path="edit">
            <a-button type="primary" @click="handleEdit">编辑</a-button>
          </permission-button>
        </div>
        
        <!-- 引用 Add 组件，只读模式 -->
        <div class="readonly-form-wrapper">
          <Add
            :key="detailVersion"
            mode="page"
            :dialogEditId="detail.id"
            :readonly="true"
          />
        </div>
      </a-card>
    </div>

    <div v-else-if="loading" class="loading-wrap">
      <a-spin />
    </div>

    <!-- 编辑弹窗 -->
    <a-modal
      title="编辑项目备案"
      :visible="editVisible"
      :width="1200"
      :footer="null"
      @cancel="editVisible = false"
    >
      <Add
        v-if="editVisible"
        mode="dialog"
        :dialogEditId="detail.id"
        @success="handleEditSuccess"
        @cancel="editVisible = false"
      />
    </a-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getProjectFilingDetail } from "@/api/market/projectFiling";
import watermark from "@/mixins/watermark";
import Add from "./add.vue";
import {canEditByAdmin} from "@/utils/columns";

export default {
  name: "ProjectFilingDetail",
  mixins: [watermark],
  components: { Add },

  data() {
    return {
      detail: {},
      loading: false,
      editVisible: false,
      detailVersion: 0,
    };
  },

  computed: {
    ...mapGetters("setting", ["findDataDict"]),

    filingStatusText() {
      const map = { 10: "待审核", 20: "审核通过", 30: "已转换", 40: "已拒绝", 80: "已撤销", 90: "已关闭" };
      return map[this.detail.filingStatus] || "—";
    },
  },

  mounted() {
    const id = this.$route.query.id;
    if (id) this.loadDetail(id);
  },

  methods: {
    canEditByAdmin,
    loadDetail(id) {
      this.loading = true;
      getProjectFilingDetail(id)
        .then(res => { this.detail = res || {}; })
        .finally(() => { this.loading = false; });
    },

    handleEdit() {
      this.editVisible = true;
    },

    handleEditSuccess() {
      this.editVisible = false;
      this.detailVersion++;
      this.loadDetail(this.detail.id);
    },
  },
};
</script>

<style lang="less" scoped>
.detail-container {
  padding: 16px;
}

.header-card {
  background: #fff;
  padding: 20px 24px 16px;
  margin-bottom: 12px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  .project-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 14px;
  }

  .header-meta {
    display: flex;
    gap: 40px;
    flex-wrap: wrap;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 6px;

      .meta-label {
        color: #999;
        font-size: 13px;
      }

      .meta-value {
        color: #333;
        font-size: 13px;
      }
    }
  }
}

.content-card {
  border-radius: 4px;
}

.tab-toolbar {
  text-align: right;
  margin-bottom: 16px;
}

.readonly-form-wrapper {
  // 只读模式样式 - 禁用所有交互
  /deep/ .container {
    padding: 0;
    background: transparent;
    
    // 隐藏提交按钮区域
    .center {
      display: none;
    }

    // 禁用所有表单控件的交互
    .ant-form {
      pointer-events: none;
      user-select: text;
    }

    // 所有输入框、下拉框等设置为只读样式
    .ant-input,
    .ant-input-number-input,
    .ant-select-selection,
    textarea.ant-input {
      background-color: #fafafa !important;
      border-color: #d9d9d9 !important;
      color: rgba(0, 0, 0, 0.85) !important;
      cursor: default !important;
    }

    // disabled 状态的输入框
    .ant-input[disabled],
    .ant-input-number-disabled .ant-input-number-input,
    .ant-select-disabled .ant-select-selection {
      background-color: #f5f5f5 !important;
      color: rgba(0, 0, 0, 0.65) !important;
    }

    // 单选框和复选框组
    .ant-radio-wrapper,
    .ant-checkbox-wrapper {
      cursor: default !important;
    }

    .ant-radio-inner,
    .ant-checkbox-inner {
      background-color: #fafafa !important;
      border-color: #d9d9d9 !important;
    }

    // 隐藏上传按钮
    .ant-btn[disabled],
    .ant-btn-primary {
      display: none !important;
    }

    // 隐藏下拉箭头
    .ant-select-arrow,
    .ant-cascader-picker-arrow,
    .ant-calendar-picker-icon {
      display: none !important;
    }

    // 日期选择器输入框
    .ant-calendar-picker-input {
      background-color: #fafafa !important;
      border-color: #d9d9d9 !important;
    }

    // 数字输入框
    .ant-input-number {
      background-color: #fafafa !important;
      border-color: #d9d9d9 !important;

      .ant-input-number-handler-wrap {
        display: none !important;
      }
    }

    // 级联选择器
    .ant-cascader-picker {
      background-color: #fafafa !important;
      
      .ant-cascader-input {
        background-color: #fafafa !important;
      }
    }

    // 文件下载链接保持可点击
    a[href][target="_blank"] {
      pointer-events: auto !important;
      cursor: pointer !important;
    }

    // 表格中的链接也保持可点击
    .ant-table {
      pointer-events: auto !important;
      
      a {
        pointer-events: auto !important;
        cursor: pointer !important;
      }
      
      // 但表格中的删除按钮仍然隐藏
      .danger {
        display: none !important;
      }
    }
  }
}

.loading-wrap {
  text-align: center;
  padding: 80px 0;
}
</style>
